import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';

const NAVY = '#010449';
const ORANGE = '#F28C28';
const BORDER = '#E4E8F0';

export const WEEK_DAYS = [
  { key: 'monday', label: 'Lundi' },
  { key: 'tuesday', label: 'Mardi' },
  { key: 'wednesday', label: 'Mercredi' },
  { key: 'thursday', label: 'Jeudi' },
  { key: 'friday', label: 'Vendredi' },
  { key: 'saturday', label: 'Samedi' },
  { key: 'sunday', label: 'Dimanche' },
];

const clone = (value) => JSON.parse(JSON.stringify(value));
const period = (open = '06:00', close = '23:00') => ({ open, close });
const closedDay = () => ({ closed: true, periods: [] });
const openDay = (open = '06:00', close = '23:00') => ({ closed: false, periods: [period(open, close)] });

const weekFrom = (weekday, weekend = weekday) => Object.fromEntries(
  WEEK_DAYS.map(({ key }) => [key, clone(['saturday', 'sunday'].includes(key) ? weekend : weekday)]),
);

export const createEmptyOpeningHours = () => ({
  schema_version: 2,
  timezone: 'Europe/Paris',
  access_mode: 'custom',
  weekly: Object.fromEntries(WEEK_DAYS.map(({ key }) => [key, closedDay()])),
  reception: {
    enabled: false,
    weekly: Object.fromEntries(WEEK_DAYS.map(({ key }) => [key, closedDay()])),
  },
  holidays: { mode: 'closed', periods: [] },
  notice: '',
});

const normalizeDay = (value) => {
  if (!value || value.closed === true) return closedDay();
  if (Array.isArray(value.periods)) {
    const periods = value.periods
      .filter((item) => item?.open && item?.close)
      .map((item) => period(item.open, item.close));
    return periods.length ? { closed: false, periods } : closedDay();
  }
  if (value.open && value.close) return openDay(value.open, value.close);
  return closedDay();
};

export const normalizeOpeningHours = (raw) => {
  const empty = createEmptyOpeningHours();
  if (!raw || typeof raw !== 'object') return empty;

  if (raw.schema_version === 2 && raw.weekly) {
    return {
      ...empty,
      ...raw,
      access_mode: raw.access_mode === '24_7' ? '24_7' : 'custom',
      weekly: Object.fromEntries(
        WEEK_DAYS.map(({ key }) => [key, normalizeDay(raw.weekly[key])]),
      ),
      reception: {
        enabled: Boolean(raw.reception?.enabled),
        weekly: Object.fromEntries(
          WEEK_DAYS.map(({ key }) => [key, normalizeDay(raw.reception?.weekly?.[key])]),
        ),
      },
      holidays: {
        mode: ['same_as_week', 'closed', 'custom'].includes(raw.holidays?.mode)
          ? raw.holidays.mode
          : 'closed',
        periods: Array.isArray(raw.holidays?.periods) ? raw.holidays.periods : [],
      },
      notice: String(raw.notice || '').slice(0, 250),
    };
  }

  // Compatibilité avec l'ancien format {monday: {open, close, closed}, ...}.
  empty.weekly = Object.fromEntries(
    WEEK_DAYS.map(({ key }) => [key, normalizeDay(raw[key])]),
  );
  if (raw.holidays && raw.holidays.closed === false) {
    empty.holidays = {
      mode: 'custom',
      periods: raw.holidays.open && raw.holidays.close
        ? [period(raw.holidays.open, raw.holidays.close)]
        : [],
    };
  }
  return empty;
};

const DayEditor = ({ day, label, onChange, onCopy, compact = false }) => {
  const isOpen = !day.closed;
  const periods = day.periods || [];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: compact ? '150px minmax(0,1fr)' : '170px minmax(0,1fr) 42px' },
        gap: 1.25,
        alignItems: 'start',
        py: 1.4,
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <FormControlLabel
        control={(
          <Switch
            checked={isOpen}
            onChange={(event) => onChange(event.target.checked ? openDay() : closedDay())}
          />
        )}
        label={<Typography fontWeight={750}>{label}</Typography>}
      />

      {isOpen ? (
        <Stack spacing={1}>
          {periods.map((item, index) => (
            <Stack
              key={`${label}-${index}`}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <TextField
                size="small" type="time" label="Ouverture" value={item.open}
                onChange={(event) => {
                  const next = clone(day);
                  next.periods[index].open = event.target.value;
                  onChange(next);
                }}
                inputProps={{ step: 300 }} sx={{ minWidth: { sm: 122 }, flex: 1 }}
              />
              <Typography color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>–</Typography>
              <TextField
                size="small" type="time" label="Fermeture" value={item.close}
                onChange={(event) => {
                  const next = clone(day);
                  next.periods[index].close = event.target.value;
                  onChange(next);
                }}
                inputProps={{ step: 300 }} sx={{ minWidth: { sm: 122 }, flex: 1 }}
              />
              {periods.length > 1 && (
                <IconButton
                  size="small" aria-label="Supprimer cette plage"
                  onClick={() => onChange({ ...day, periods: periods.filter((_, itemIndex) => itemIndex !== index) })}
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          ))}
          {periods.length < 3 && (
            <Button
              size="small" startIcon={<AddRoundedIcon />} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
              onClick={() => onChange({ ...day, periods: [...periods, period('14:00', '18:00')] })}
            >
              Ajouter une plage
            </Button>
          )}
        </Stack>
      ) : (
        <Chip label="Fermé" size="small" sx={{ justifySelf: 'start', mt: 0.7 }} />
      )}

      {!compact && onCopy && (
        <Tooltip title="Appliquer cet horaire à toute la semaine">
          <IconButton onClick={onCopy} size="small" sx={{ mt: 0.5 }}>
            <ContentCopyRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

const standardReception = () => ({
  enabled: true,
  weekly: {
    ...weekFrom(openDay('09:00', '20:00'), openDay('09:00', '18:00')),
    sunday: closedDay(),
  },
});

const applyPreset = (schedule, presetName) => {
  const next = clone(schedule);
  if (presetName === '24_7') {
    next.access_mode = '24_7';
    return next;
  }
  next.access_mode = 'custom';
  if (presetName === 'extended') {
    next.weekly = weekFrom(openDay('06:00', '23:00'));
  } else {
    next.weekly = weekFrom(openDay('06:00', '22:30'), openDay('09:00', '19:00'));
  }
  next.reception = standardReception();
  return next;
};

const OpeningHoursEditor = ({ value, onChange, gymSex, busy, onSave }) => {
  const defaultAudience = gymSex === 'women' ? 'women' : 'men';
  const [audience, setAudience] = useState(defaultAudience);
  const schedule = value[audience];
  const showAudienceChoice = gymSex === 'both';

  const title = useMemo(() => (
    audience === 'women' ? 'Horaires pour les femmes' : 'Horaires pour les hommes'
  ), [audience]);

  const setSchedule = (nextSchedule) => {
    if (value.sameForAll) {
      onChange({ ...value, men: clone(nextSchedule), women: clone(nextSchedule) });
    } else {
      onChange({ ...value, [audience]: nextSchedule });
    }
  };

  const updateWeeklyDay = (key, day) => {
    setSchedule({ ...schedule, weekly: { ...schedule.weekly, [key]: day } });
  };

  const updateReceptionDay = (key, day) => {
    setSchedule({
      ...schedule,
      reception: {
        ...schedule.reception,
        weekly: { ...schedule.reception.weekly, [key]: day },
      },
    });
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" fontWeight={900} color={NAVY}>Horaires de la salle</Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
          Distinguez l’accès au club de la présence de l’accueil. Les horaires sont affichés en heure de Paris.
        </Typography>
      </Box>

      {showAudienceChoice && (
        <Card variant="outlined" sx={{ borderRadius: 3, borderColor: BORDER }}>
          <CardContent>
            <FormControlLabel
              control={(
                <Switch
                  checked={value.sameForAll}
                  onChange={(event) => {
                    const sameForAll = event.target.checked;
                    onChange({
                      ...value,
                      sameForAll,
                      ...(sameForAll ? { men: clone(schedule), women: clone(schedule) } : {}),
                    });
                  }}
                />
              )}
              label="Utiliser les mêmes horaires pour tous les publics"
            />
            {!value.sameForAll && (
              <Tabs value={audience} onChange={(_, next) => setAudience(next)} sx={{ mt: 1 }}>
                <Tab value="men" label="Hommes" />
                <Tab value="women" label="Femmes" />
              </Tabs>
            )}
          </CardContent>
        </Card>
      )}

      <Card variant="outlined" sx={{ borderRadius: 3, borderColor: BORDER }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
            <Box>
              <Typography variant="h6" fontWeight={850}>{value.sameForAll ? 'Horaires d’accès' : title}</Typography>
              <Typography variant="body2" color="text.secondary">Quand les abonnés peuvent entrer et s’entraîner.</Typography>
            </Box>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Chip clickable label="24h/24 – 7j/7" onClick={() => setSchedule(applyPreset(schedule, '24_7'))} />
              <Chip clickable label="6h–23h tous les jours" onClick={() => setSchedule(applyPreset(schedule, 'extended'))} />
              <Chip clickable label="Semaine + week-end réduit" onClick={() => setSchedule(applyPreset(schedule, 'standard'))} />
            </Stack>
          </Stack>

          <Alert severity="info" icon={<AccessTimeRoundedIcon />} sx={{ mt: 2, borderRadius: 2 }}>
            {schedule.access_mode === '24_7'
              ? 'Le club sera présenté comme accessible 24h/24 et 7j/7.'
              : 'Vous pouvez ajouter jusqu’à trois plages par jour, par exemple 06:00–12:00 puis 14:00–22:00.'}
          </Alert>

          {schedule.access_mode !== '24_7' && (
            <Box sx={{ mt: 1 }}>
              {WEEK_DAYS.map(({ key, label }) => (
                <DayEditor
                  key={key} label={label} day={schedule.weekly[key]}
                  onChange={(day) => updateWeeklyDay(key, day)}
                  onCopy={() => setSchedule({
                    ...schedule,
                    weekly: Object.fromEntries(WEEK_DAYS.map((item) => [item.key, clone(schedule.weekly[key])])),
                  })}
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ borderRadius: 3, borderColor: BORDER }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
            <Box>
              <Typography variant="h6" fontWeight={850}>Accueil et équipe sur place</Typography>
              <Typography variant="body2" color="text.secondary">
                Utile lorsqu’un badge permet l’accès au club en dehors des heures d’accueil.
              </Typography>
            </Box>
            <FormControlLabel
              control={(
                <Switch
                  checked={schedule.reception.enabled}
                  onChange={(event) => setSchedule({
                    ...schedule,
                    reception: {
                      ...(event.target.checked ? standardReception() : schedule.reception),
                      enabled: event.target.checked,
                    },
                  })}
                />
              )}
              label={schedule.reception.enabled ? 'Horaires affichés' : 'Non renseigné'}
            />
          </Stack>

          {schedule.reception.enabled && (
            <Box sx={{ mt: 1 }}>
              {WEEK_DAYS.map(({ key, label }) => (
                <DayEditor
                  compact key={key} label={label} day={schedule.reception.weekly[key]}
                  onChange={(day) => updateReceptionDay(key, day)}
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ borderRadius: 3, borderColor: BORDER }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <EventAvailableRoundedIcon sx={{ color: ORANGE }} />
            <Typography variant="h6" fontWeight={850}>Jours fériés et information exceptionnelle</Typography>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <TextField
                select fullWidth label="Règle des jours fériés" value={schedule.holidays.mode}
                onChange={(event) => setSchedule({
                  ...schedule,
                  holidays: { ...schedule.holidays, mode: event.target.value },
                })}
              >
                <MenuItem value="same_as_week">Même horaire que le jour concerné</MenuItem>
                <MenuItem value="closed">Fermé les jours fériés</MenuItem>
                <MenuItem value="custom">Horaire spécifique</MenuItem>
              </TextField>
            </Grid>
            {schedule.holidays.mode === 'custom' && (
              <Grid item xs={12} md={7}>
                <DayEditor
                  compact label="Jour férié"
                  day={{ closed: false, periods: schedule.holidays.periods.length ? schedule.holidays.periods : [period('09:00', '19:00')] }}
                  onChange={(day) => setSchedule({
                    ...schedule,
                    holidays: { ...schedule.holidays, periods: day.periods },
                  })}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth multiline minRows={2} inputProps={{ maxLength: 250 }}
                label="Message public facultatif"
                placeholder="Ex. Accès de nuit réservé aux membres munis de leur badge."
                value={schedule.notice}
                onChange={(event) => setSchedule({ ...schedule, notice: event.target.value })}
                helperText={`${schedule.notice.length}/250 caractères`}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider />
      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained" disabled={busy} onClick={onSave}
          startIcon={<SaveRoundedIcon />}
          sx={{ bgcolor: NAVY, borderRadius: 2.5, px: 3, py: 1.2, textTransform: 'none', fontWeight: 800 }}
        >
          {busy ? 'Enregistrement…' : 'Enregistrer les horaires'}
        </Button>
      </Stack>
    </Stack>
  );
};

export default OpeningHoursEditor;
